import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, X, Settings } from 'lucide-react';
import AthleteSearchFilter from '../components/AthleteSearchFilter';
import ClearanceBadge from '../components/ClearanceBadge';
import { AthleteAvatar } from '../components/AthleteAvatar';
import { timeAgo } from '../utils/relativeTime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POSITIONS = [
  'Goalkeeper', 'Right Back', 'Center Back', 'Left Back', 'Defender',
  'Defensive Midfielder', 'Central Midfielder', 'Midfielder',
  'Attacking Midfielder', 'Right Winger', 'Left Winger', 'Forward', 'Striker'
];
const SQUADS = ['U15', 'U17', 'U19', 'U21', 'Senior', 'Reserve'];
const FEET = ['Right', 'Left', 'Both'];
const GENDER_OPTIONS = ['Male', 'Female', 'Other', 'Prefer not to say'];

const TEST_PLAYER_ID = '0e378572-d49c-4bd2-8a92-6a166f84620f';

const StaffAthletes = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [athletes, setAthletes] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagMap, setTagMap] = useState({});
  const [coachRows, setCoachRows] = useState({}); // keyed by athlete_id
  const [statusTab, setStatusTab] = useState('active'); // active | inactive | all
  const [smartFilters, setSmartFilters] = useState([]); // ['injured','no-wellness','missed-last']
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newAthlete, setNewAthlete] = useState({
    full_name: '', date_of_birth: '', nationality: '', position: '',
    dominant_foot: '', squad: '', email: '', phone: '', gender: ''
  });
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchMyAthletes();
  }, [statusTab]);

  const fetchMyAthletes = async () => {
    try {
      const [athRes, tagsRes, mapRes, coachRes] = await Promise.all([
        axios.get(`${API}/athletes?status=${statusTab}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/tags`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/tags/map`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
        axios.get(`${API}/dashboard/coach-today`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
      ]);
      setAthletes(athRes.data);
      setTags(tagsRes.data || []);
      setTagMap(mapRes.data || {});
      const rows = coachRes.data?.athlete_rows || [];
      const rowMap = {};
      rows.forEach(r => { rowMap[r.id] = r; });
      setCoachRows(rowMap);
    } catch (error) {
      toast.error('Failed to fetch athletes');
    } finally {
      setLoading(false);
    }
  };

  const toggleSmart = (key) => {
    setSmartFilters(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const toggleTagFilter = (id) => {
    setSelectedTagIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleCreate = async () => {
    if (!newAthlete.full_name?.trim()) { toast.error('Name is required'); return; }
    if (!newAthlete.date_of_birth) { toast.error('Date of birth is required'); return; }
    if (!newAthlete.nationality?.trim()) { toast.error('Nationality is required'); return; }
    if (!newAthlete.position) { toast.error('Position is required'); return; }

    setCreating(true);
    try {
      const payload = { ...newAthlete };
      if (!payload.email) delete payload.email;
      if (!payload.phone) delete payload.phone;
      if (!payload.dominant_foot) delete payload.dominant_foot;
      if (!payload.squad) delete payload.squad;
      if (!payload.gender) delete payload.gender;

      await axios.post(`${API}/athletes`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Athlete created and auto-assigned to you');
      setShowCreate(false);
      setNewAthlete({ full_name: '', date_of_birth: '', nationality: '', position: '', dominant_foot: '', squad: '', email: '', phone: '', gender: '' });
      fetchMyAthletes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create athlete');
    } finally {
      setCreating(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      inactive: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600'
    };
    return colors[status] || colors.active;
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              My Athletes
            </h2>
            <p className="text-gray-600 dark:text-gray-500 mt-1">Athletes assigned to you</p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-pfap-green hover:bg-pfap-green/90 text-white" data-testid="staff-create-athlete-btn">
            <Plus size={16} className="mr-1.5" /> Create Athlete
          </Button>
        </div>

        {/* Status tabs */}
        <div className="flex items-center gap-1.5 mb-3" data-testid="athlete-status-tabs">
          {[
            { key: 'active', label: 'Active' },
            { key: 'inactive', label: 'Inactive' },
            { key: 'all', label: 'All' },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setStatusTab(t.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                statusTab === t.key
                  ? 'bg-pfap-green text-white border-pfap-green shadow-sm'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
              }`}
              data-testid={`status-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Smart filters */}
        <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid="smart-filter-chips">
          {[
            { key: 'injured', label: 'Injured' },
            { key: 'no-wellness', label: 'No wellness today' },
            { key: 'missed-last', label: 'Missed last session' },
            { key: 'pdp-overdue', label: 'PDP overdue' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => toggleSmart(f.key)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors border ${
                smartFilters.includes(f.key)
                  ? 'bg-red-500 text-white border-red-500 shadow-sm'
                  : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
              }`}
              data-testid={`smart-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
          {tags.length > 0 && (
            <span className="mx-2 text-gray-300">|</span>
          )}
          {tags.map(t => {
            const on = selectedTagIds.includes(t.id);
            return (
              <button
                key={t.id}
                onClick={() => toggleTagFilter(t.id)}
                className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border transition ${on ? 'text-white shadow-sm' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                style={on ? { background: t.color || '#10b981', borderColor: t.color || '#10b981' } : { borderColor: (t.color || '#64748b') + '66' }}
                data-testid={`smart-tag-filter-${t.id}`}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: on ? '#fff' : (t.color || '#64748b') }} />
                {t.name}
              </button>
            );
          })}
          {(smartFilters.length > 0 || selectedTagIds.length > 0) && (
            <button
              onClick={() => { setSmartFilters([]); setSelectedTagIds([]); }}
              className="text-[11px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 ml-1"
              data-testid="smart-clear-filters"
            >
              Clear
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        ) : athletes.length === 0 ? (
          <Card data-testid="no-athletes-message">
            <CardContent className="py-12 text-center">
              <p className="text-gray-500 dark:text-gray-500">No athletes assigned to you yet. Contact an administrator.</p>
            </CardContent>
          </Card>
        ) : (
          <AthleteSearchFilter athletes={athletes} searchFirst>
            {(filtered) => {
              // Apply smart filter chips + tag chips
              const applied = filtered.filter(a => {
                const row = coachRows[a.id] || {};
                if (smartFilters.includes('injured') && row.availability !== 'Injured') return false;
                if (smartFilters.includes('no-wellness') && row.wellness_today !== false) return false;
                if (smartFilters.includes('missed-last') && !row.missed_last_session) return false;
                if (smartFilters.includes('pdp-overdue') && !row.pdp_overdue) return false;
                if (selectedTagIds.length > 0) {
                  const aTags = tagMap[a.id] || [];
                  if (!selectedTagIds.some(t => aTags.includes(t))) return false;
                }
                return true;
              });
              // Pin Test Player to the top for Staff (onboarding reference)
              const sorted = [...applied].sort((a, b) => {
                if (a.id === TEST_PLAYER_ID && b.id !== TEST_PLAYER_ID) return -1;
                if (b.id === TEST_PLAYER_ID && a.id !== TEST_PLAYER_ID) return 1;
                return 0;
              });
              return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="athletes-grid">
                {sorted.length === 0 ? (
                  <div className="col-span-full text-center py-8 text-gray-500" data-testid="no-match-filters">No athletes match your filters</div>
                ) : sorted.map((athlete) => {
                  const row = coachRows[athlete.id] || {};
                  return (
              <Card key={athlete.id} className="hover:shadow-lg transition-shadow" data-testid={`athlete-card-${athlete.id}`}>
                <CardHeader>
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex items-start gap-2.5 min-w-0">
                      <AthleteAvatar
                        photoUrl={athlete.profile_photo}
                        thumbUrl={athlete.profile_photo_thumbnail}
                        name={athlete.full_name}
                        size="md"
                        className="flex-shrink-0"
                        testId={`athlete-avatar-${athlete.id}`}
                      />
                      <div className="min-w-0">
                        <CardTitle className="text-xl flex items-center gap-2" data-testid={`athlete-name-${athlete.id}`}>
                          {athlete.full_name}
                          {athlete.id === TEST_PLAYER_ID && (
                            <Badge
                              className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700"
                              data-testid="demo-badge"
                              title="Onboarding reference profile — visible only to Admin/Staff"
                            >
                              DEMO
                            </Badge>
                          )}
                        </CardTitle>
                        <CardDescription>{athlete.position}{athlete.squad ? ` · ${athlete.squad}` : ''}</CardDescription>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge className={getStatusBadge(athlete.status)} data-testid={`athlete-status-${athlete.id}`}>
                        {athlete.status}
                      </Badge>
                      {athlete.status === 'active' && row.availability ? (
                        <ClearanceBadge value={row.availability} testId={`athlete-clearance-${athlete.id}`} />
                      ) : null}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2.5">
                  {/* Decision signals */}
                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    <div className="rounded-md bg-gray-50 dark:bg-gray-900/40 p-2" data-testid={`signal-wellness-${athlete.id}`}>
                      <p className="text-[9px] uppercase tracking-wide text-gray-400">Last wellness</p>
                      {row.last_wellness ? (
                        <p className="font-semibold text-gray-900 dark:text-gray-100">
                          {row.last_wellness.overall_score}/10
                          <span className="text-[10px] text-gray-400 ml-1" title={row.last_wellness.date}>{timeAgo(row.last_wellness.date)}</span>
                        </p>
                      ) : (
                        <p className="text-gray-400">No data</p>
                      )}
                    </div>
                    <div className="rounded-md bg-gray-50 dark:bg-gray-900/40 p-2" data-testid={`signal-match-${athlete.id}`}>
                      <p className="text-[9px] uppercase tracking-wide text-gray-400">Last match</p>
                      {row.last_match ? (
                        <p className="font-semibold text-gray-900 dark:text-gray-100">
                          {row.last_match.rating != null ? `${row.last_match.rating}/10` : '—'}
                          <span className="text-[10px] text-gray-400 ml-1">{row.last_match.date}</span>
                        </p>
                      ) : (
                        <p className="text-gray-400">No data</p>
                      )}
                    </div>
                  </div>

                  {/* Inline signals */}
                  <div className="flex flex-wrap gap-1" data-testid={`signal-chips-${athlete.id}`}>
                    {row.wellness_today === false && (
                      <span className="text-[10px] px-1.5 py-0 rounded-full bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800">No wellness today</span>
                    )}
                    {row.missed_last_session && (
                      <span className="text-[10px] px-1.5 py-0 rounded-full bg-orange-50 text-orange-700 border border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800">Missed last session</span>
                    )}
                    {row.pdp_overdue && (
                      <span className="text-[10px] px-1.5 py-0 rounded-full bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800">PDP overdue</span>
                    )}
                    {row.injury?.body_part && (
                      <span className="text-[10px] px-1.5 py-0 rounded-full bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800">
                        {row.injury.body_part}{row.injury.severity ? ` · ${row.injury.severity}` : ''}
                      </span>
                    )}
                  </div>

                  {(tagMap[athlete.id] || []).length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap" data-testid={`athlete-tags-${athlete.id}`}>
                      {(tagMap[athlete.id] || []).slice(0, 4).map(tid => {
                        const t = tags.find(tg => tg.id === tid);
                        if (!t) return null;
                        return (
                          <span
                            key={tid}
                            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border"
                            style={{ borderColor: (t.color || '#64748b') + '66', color: t.color || '#475569', background: (t.color || '#64748b') + '14' }}
                            title={t.description || t.name}
                          >
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color || '#64748b' }} />
                            {t.name}
                          </span>
                        );
                      })}
                    </div>
                  )}

                  <div className="flex gap-2 pt-1">
                    <Button
                      className="flex-1 bg-pfap-green hover:bg-pfap-green/90"
                      data-testid={`view-profile-${athlete.id}`}
                      onClick={() => navigate(`/athlete/${athlete.id}/360`)}
                    >
                      View 360°
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/athletes/${athlete.id}/manage`)}
                      data-testid={`manage-${athlete.id}`}
                      className="h-10 w-10 p-0"
                    >
                      <Settings size={16} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
                  );
                })}
              </div>
              );
            }}
          </AthleteSearchFilter>
        )}

        {/* Create Athlete Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="staff-create-athlete-modal">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Create New Athlete</h2>
                <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <X size={20} />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-700 dark:text-gray-300">Full Name *</Label>
                    <Input value={newAthlete.full_name} onChange={e => setNewAthlete(p => ({...p, full_name: e.target.value}))}
                      placeholder="Full Name" data-testid="staff-create-input-name" />
                  </div>
                  <div>
                    <Label className="text-gray-700 dark:text-gray-300">Date of Birth *</Label>
                    <Input type="date" value={newAthlete.date_of_birth}
                      onChange={e => setNewAthlete(p => ({...p, date_of_birth: e.target.value}))}
                      data-testid="staff-create-input-dob" />
                  </div>
                  <div>
                    <Label className="text-gray-700 dark:text-gray-300">Nationality *</Label>
                    <Input value={newAthlete.nationality} onChange={e => setNewAthlete(p => ({...p, nationality: e.target.value}))}
                      placeholder="Nationality" data-testid="staff-create-input-nationality" />
                  </div>
                  <div>
                    <Label className="text-gray-700 dark:text-gray-300">Position *</Label>
                    <select value={newAthlete.position} onChange={e => setNewAthlete(p => ({...p, position: e.target.value}))}
                      className="w-full h-10 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
                      data-testid="staff-create-select-position">
                      <option value="">Select Position</option>
                      {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-gray-700 dark:text-gray-300">Dominant Foot</Label>
                    <select value={newAthlete.dominant_foot} onChange={e => setNewAthlete(p => ({...p, dominant_foot: e.target.value}))}
                      className="w-full h-10 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
                      data-testid="staff-create-select-foot">
                      <option value="">Select Foot</option>
                      {FEET.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-gray-700 dark:text-gray-300">Gender</Label>
                    <select value={newAthlete.gender} onChange={e => setNewAthlete(p => ({...p, gender: e.target.value}))}
                      className="w-full h-10 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
                      data-testid="staff-create-select-gender">
                      <option value="">— Not set —</option>
                      {GENDER_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-gray-700 dark:text-gray-300">Squad / Team</Label>
                    <select value={newAthlete.squad} onChange={e => setNewAthlete(p => ({...p, squad: e.target.value}))}
                      className="w-full h-10 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
                      data-testid="staff-create-select-squad">
                      <option value="">Select Squad</option>
                      {SQUADS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-gray-700 dark:text-gray-300">Email (optional)</Label>
                    <Input type="email" value={newAthlete.email}
                      onChange={e => setNewAthlete(p => ({...p, email: e.target.value}))}
                      placeholder="athlete@email.com" data-testid="staff-create-input-email" />
                  </div>
                  <div>
                    <Label className="text-gray-700 dark:text-gray-300">Phone (optional)</Label>
                    <Input value={newAthlete.phone} onChange={e => setNewAthlete(p => ({...p, phone: e.target.value}))}
                      placeholder="+351 000 000 000" data-testid="staff-create-input-phone" />
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
                <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                <Button onClick={handleCreate} disabled={creating}
                  className="bg-pfap-green hover:bg-pfap-green/90 text-white" data-testid="staff-create-submit-btn">
                  {creating ? 'Creating...' : 'Create Athlete'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StaffAthletes;
